// How much a transaction moved, as opposed to how much it spent.
//
// Bitcoin has no notion of change, so a transaction spending a large output has
// to send the remainder back to its owner as another output. Totalling the
// outputs counts that remainder as though it had gone somewhere: spending 2 BTC
// from a 100 BTC output reads as a 100 BTC transfer.
//
// At the top of the range that stops being a rounding error and becomes the
// whole figure. Measured over 30 blocks, transfers of 1,000 BTC or more ran 86 a
// day by output total and *none at all* by net value - every one of them was a
// single wallet sending about 2,464 BTC to itself once a block, and the bot
// posted two of them to a public channel as "moved between unknown wallets"
// before anyone noticed the sender and the recipient were the same address.
//
// Net value is the total of the outputs that went somewhere the coins did not
// come from. It is an approximation and worth knowing the shape of:
//
//   - A wallet that takes change to a fresh address - which good wallets do -
//     still reads as a payment, so this overstates some. Batch payouts, where
//     it is most repetitive, are caught by batchChange below; ordinary
//     one-to-one transfers are left alone. That does not make the rest small:
//     a two-output transfer with fresh change once read as 1,139 BTC when 125
//     moved. README -> Whale alerts -> What net value gets wrong has the cases
//     seen and why this is left as it is.
//   - Coins genuinely sent to another address the same owner controls read as
//     change and are not counted, which is the intended reading here anyway.
//   - When no input address can be resolved there is nothing to subtract and
//     this returns the output total, which is the conservative answer: an
//     unreadable transaction is not evidence of a self-send.
//
// Imports nothing on purpose, so the browser and Node can share it. BtcLive
// reads the websocket feed and the whale alert bot reads mined blocks; both
// shapes carry inputs[].prev_out.addr and out[].addr, which is all this needs. A
// second copy would drift, and the page and the bot would then disagree about
// what counts as a whale - the same reason exchangeFlow.js lives on its own.

// The script type an address implies, from its prefix. Wallets overwhelmingly
// send change to the same type they spend from, which is one of the tells below.
const scriptType = (address) => {
  if (!address) return null;
  if (address.startsWith("bc1p")) return "p2tr";
  if (address.startsWith("bc1q")) return address.length > 50 ? "p2wsh" : "p2wpkh";
  if (address.startsWith("3")) return "p2sh";
  if (address.startsWith("1")) return "p2pkh";
  return null;
};

// 0.01 BTC. A payment is usually a number somebody chose; change is whatever was
// left over, and almost never lands on a round hundredth.
const ROUND = 1000000;

// Batch payouts with their change sent to a fresh address.
//
// The rule above only recognises change that returns to an address the coins
// came from. Wallets that do the right thing and use a new change address slip
// past it, and one kind of sender makes that very visible: a service paying out
// a batch of customer withdrawals. Seeding Bitcoin Live's panels put six of these
// on the front page at once - one address, fifteen outputs of 0.01 to 1 BTC, and
// 63 BTC of change in a sixteenth - each reading as a 64 BTC whale when about
// half a coin had moved.
//
// So one output is also treated as change when every one of these holds:
//
//   - one sending address, so it is plainly one wallet paying out
//   - five or more outputs, which is a batch rather than a transfer
//   - that output is 95% or more of everything sent
//   - it is not a round amount
//   - it is the same script type as the input
//
// Measured over blocks 966852-966863: 17 batch-shaped transactions over 10 BTC,
// 15 treated as change, 2 kept by the script type check (a p2sh input paying a
// 43.50 BTC p2wpkh output, and a p2wpkh input paying 12.50 BTC to p2pkh - both
// plausibly real payments, so both still count).
//
// Two-output transfers are excluded outright, not by measurement: 181.00 BTC
// beside 1.000 BTC could be a payment of either, and there is no telling.
//
// The round-amount check kept nothing in that sample and stays as a guard. A
// batch whose big line is exactly 10.00000000 BTC is somebody's chosen number.
// It is exact to the hundredth on purpose: outputs that looked like "50.00" and
// "20.00" at two decimals were 50.00161847 and 20.00387080, and each went on to
// be the sending address of the next payout batch - change working its way down
// a peel chain, which is exactly what this is meant to catch.
//
// It is still a heuristic and it errs on one side. A batch whose largest line
// genuinely is a customer's 63.45 BTC withdrawal would be undercounted. That is
// the lesser error for a whale feed: missing one real transfer costs a post,
// while a payout wallet peeling change reads as a stream of whales that never
// happened - which is how the first alerts this site posted went wrong.
const batchChange = (outputs, senders) => {
  if (senders.size !== 1 || outputs.length < 5) return null;

  const total = outputs.reduce((sum, output) => sum + (output?.value || 0), 0);
  if (total === 0) return null;

  const largest = outputs.reduce((a, b) => ((b?.value || 0) > (a?.value || 0) ? b : a));
  if (largest.value / total < 0.95) return null;
  if (largest.value % ROUND === 0) return null;

  const [sender] = senders;
  const type = scriptType(largest.addr);
  if (!type || type !== scriptType(sender)) return null;

  return largest;
};

export const netMovedSats = (tx) => {
  const senders = new Set(
    (tx.inputs || []).map((input) => input?.prev_out?.addr).filter(Boolean)
  );

  // An output with no address is an OP_RETURN or a non-standard script. It
  // cannot be matched against the senders, and it carries no value worth
  // counting, so it falls through the check and adds its zero.
  const leaving = (tx.out || []).filter(
    (output) => !(output?.addr && senders.has(output.addr))
  );

  const change = batchChange(leaving, senders);

  return leaving.reduce(
    (total, output) => (output === change ? total : total + (output?.value || 0)),
    0
  );
};
